"use client";

import ProductPage from "@/components/ProductPage";
import { KassensystemHardwareSection } from "@/components/KassensystemHardwareSection";
import ImageHero from "./Getraenkehandel.png";
import ImageHighlight from "./highlight.jpg";
import ImageFeaturePOS from "./feature-pos.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import { Check } from "lucide-react";
import { PricingCard } from "@/components/PricingSection";
import Image from "next/image";

export default function Page() {
  return (
    <ProductPage
      productName="Cashtex Profi – Kassensystem für Getränkemärkte"
      hero={{
        title: "Cashtex Profi – Kassensystem für Getränkemärkte",
        subtitle:
          "Speziell für den Getränkehandel entwickelt: Pfandverwaltung, Kasten- und Mischkisten, intuitives Kassenhandling und Statistiken in Echtzeit.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: ImageHero,
        imageAlt: "Cashtex Profi Kassensystem für Getränkemärkte",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Pfand & Mehrweg</strong> inklusive Kasten- und Mischkisten –
            automatisch berechnet.
          </>,
          <>
            <strong>Schneller Verkauf</strong> für hohe Frequenz, Rücknahmen und
            Gutschriften.
          </>,
          <>
            <strong>Statistiken in Echtzeit</strong> nach Artikel, Marke,
            Lieferant, Tag oder Filiale.
          </>,
          <>
            <strong>
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                className="text-brand-primary underline hover:opacity-80"
              >
                GoBD
              </a>
              /
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                className="text-brand-primary underline hover:opacity-80"
              >
                GDPdU
              </a>
              -konform
            </strong>{" "}
            inkl. Kassenbuch und{" "}
            <a
              href="https://www.softguide.de/funktion/datev-export"
              className="text-brand-primary underline hover:opacity-80"
            >
              DATEV
            </a>
            /
            <a
              href="https://www.softguide.de/funktion/fibu-export"
              className="text-brand-primary underline hover:opacity-80"
            >
              FIBU-Export
            </a>
            /
            <a
              href="https://www.seeburger.com/de/ressourcen/wissenswertes/was-ist-edi"
              className="text-brand-primary underline hover:opacity-80"
            >
              EDI-Schnittstelle
            </a>
            /
            <a
              href="https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Schutz-vor-Manipulation-an-digitalen-Grundaufzeichnungen/Fragen-und-Antworten/fragen-und-antworten_node.html"
              className="text-brand-primary underline hover:opacity-80"
            >
              TSE
            </a>
            -konform/Schnittstelle zu Ihrem Steuerberater.
          </>,
          <>
            <strong>Finanzamt- und Gesetzkonform</strong>
          </>,
        ],
      }}
      beforeVideo={
        <>
          <Section variant="transparent" className="py-4 md:py-6 mt-12">
            <ImageText
              imagePosition="right"
              imageSlot={
                <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                  <video className="h-full w-full object-cover" controls>
                    <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                  </video>
                </div>
              }
            >
              <div className="flex flex-col gap-2 mb-2">
                <Image
                  src="/CTXr.svg"
                  alt="Cashtex"
                  width={120}
                  height={36}
                  className="object-contain"
                />
                <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                  Kassensystem für Getränkemärkte
                </h2>
              </div>

              <p className="text-slate-700 leading-relaxed">
                Cashtex wurde speziell für Getränkemärkte entwickelt und
                unterstützt Sie bei der Verwaltung von Pfand, Gebinden und
                Verkaufsprozessen – schnell und zuverlässig im Alltag.
              </p>

              <p className="mt-2 text-slate-700 leading-relaxed">
                Mit automatischer Pfandberechnung und umfassenden Statistiken
                behalten Sie jederzeit den Überblick über Umsätze und Bestände.
              </p>

              <ul className="mt-2 space-y-2">
                <li className="flex items-start gap-3 text-slate-700">
                  <Check className="mt-1 text-brand-primary" size={18} />
                  <span>
                    <strong>Pfandverwaltung:</strong> Automatische Berechnung
                    für Einweg, Mehrweg und Mischkisten.
                  </span>
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check className="mt-1 text-brand-primary" size={18} />
                  <span>
                    <strong>Schnelle Abläufe:</strong> Ideal für hohe Frequenz
                    und schnellen Kassenbetrieb.
                  </span>
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check className="mt-1 text-brand-primary" size={18} />
                  <span>
                    <strong>Volle Übersicht:</strong> Umsätze, Bestände und
                    Trends jederzeit im Blick.
                  </span>
                </li>
              </ul>
            </ImageText>
          </Section>

          <SpecialFeaturesSection
            industries={[
              {
                title: "Einzelhandel – Getränke",
                standard: [
                  "Kassieren & Bon-Druck",
                  "Artikelverwaltung",
                  "Kundenverwaltung",
                  "Tagesabschlüsse",
                  "Zahlungsarten & Belegausgabe",
                  "Rabatte & Aktionspreise",
                  "Auswertungen & Berichte",
                  "Mehrwertsteuer-Verwaltung",
                ],
                special: [
                  "Pfandverwaltung ohne manuelles Nachrechnen",
                  "Kisten- und Leergutprozesse direkt im Verkauf integriert",
                  "Schnelle Mengenauswahl für typische Verkaufsabläufe",
                  "Barcode-optimierte Abläufe für hohe Geschwindigkeit an der Kasse",
                  "Automatische Lagerbestandsüberwachung mit Mindestmengen",
                  "Lieferantenverwaltung für schnelle Nachbestellungen",
                  "Übersicht über meistverkaufte Artikel und Umsatz pro Kategorie",
                  "Kassenjournal und GoBD-konforme Archivierung",
                ],
              },
            ]}
          />
        </>
      }
      faqs={[
        {
          q: "Unterstützt Cashtex die Pfandverwaltung?",
          a: "Ja. Sie können Pfand, Kasten- und Mischkisten flexibel verwalten – mit automatischer Berechnung direkt an der Kasse, ohne manuelles Nachrechnen.",
        },
        {
          q: "Kann ich Kästen komfortabel abrechnen?",
          a: "Ja. Cashtex bietet spezielle Kastenberechnungen für Rücknahme, Erstattung und Pfandabwicklung – schnell und fehlerfrei auch bei hohem Kundenandrang.",
        },
        {
          q: "Wie funktioniert die Statistik im Getränkebereich?",
          a: "Umsatzgrafiken und Tabellen zeigen Ihnen Umsatz nach Tag, Artikel, Kunden oder Lieferanten – auch im Vergleich zum Vorjahr, für eine fundierte Sortiments- und Einkaufsplanung.",
        },
        {
          q: "Welche Berichte stehen zur Verfügung?",
          a: "Verkaufsberichte, Mitarbeitenden- und Lieferantenabrechnungen, Renner-/Penner-Listen und Umsatzlisten sind enthalten – exportierbar für Steuerberater und Buchhaltung.",
        },
        {
          q: "Wie starte ich mit der Demoversion?",
          a: "Einfach unverbindlich anfragen – wir melden uns zeitnah mit einem Terminvorschlag und richten die Demo individuell für Sie ein.",
        },
        {
          q: "Unterstützt Cashtex die Lieferantenverwaltung für schnelle Nachbestellungen?",
          a: "Ja, Lieferanten können direkt im System verwaltet werden – inklusive Bestellhistorie und automatischer Lagerbestandsüberwachung mit Mindestmengen.",
        },
        {
          q: "Kann ich Kundenkonten und Stammkunden verwalten?",
          a: "Ja, Cashtex bietet eine Kundenverwaltung mit Kaufhistorie – ideal für Stammkunden, Sonderpreise oder individuelle Konditionen.",
        },
        {
          q: "Wie läuft die Einrichtung und Schulung ab?",
          a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind im Einführungsprozess inbegriffen.",
        },
      ]}
      features={[
        {
          src: ImageFeaturePOS,
          alt: "Cashtex Getränke – schneller Verkauf und Pfand an der Kasse",
          title: "Schneller Verkauf & Kassenprozess",
          desc: "Pfand, Mehrweggebinde, Gutschriften und Belegdruck – reibungslos direkt an der Kasse für Getränkemarkt & Kiosk.",
        },
        {
          src: ImageFeatureCustomers,
          alt: "Cashtex Getränke – Kundenverwaltung und Firmenkunden",
          title: "Kundenverwaltung",
          desc: "Stammkundenpreise, Bonusprogramme, Firmenkundenabrechnungen und Lieferservice-Optionen einfach verwalten.",
        },
        {
          src: ImageFeatureStock,
          alt: "Cashtex Getränke – Lagerverwaltung und Gebindegrößen",
          title: "Artikel- & Lagerverwaltung",
          desc: "Mehrweg-/Einwegpfand, Gebindegrößen, Barcode- & Etikettendruck, automatische Bestandskontrolle und Scannerinventur.",
        },
        {
          src: ImageFeatureStats,
          alt: "Cashtex Getränke – Statistiken nach Marken und Lieferanten",
          title: "Verkaufsstatistiken",
          desc: "Umsatz pro Produkt, Renner-/Pennerlisten sowie Auswertungen nach Marken, Lieferanten und Filialen.",
        },
        {
          src: ImageFeatureOrder,
          alt: "Cashtex Getränke – Bestellwesen und Wareneingang",
          title: "Bestellwesen & Wareneingang",
          desc: "Lieferantenbestellungen für Bier, Wasser & Co., Wareneingang per Scanner und Kontrolle offener Bestellungen.",
        },
        {
          src: ImageFeatureExport,
          alt: "Cashtex Getränke – Kassenbuch, Pfandabrechnung und Export",
          title: "Kassenbuch & Export",
          desc: "Pfandabrechnung, Einnahmen/Ausgaben, revisionssicheres Kassenbuch und Exporte für DATEV, Lexware und FIBU.",
        },
      ]}
    >
      {/* PRICING CARDS */}
      <Section className="pb-24 bg-white border-y border-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
          <PricingCard
            title="Leasing ab"
            priceFull="24"
            priceCents="90"
            details="Basierend auf 0,83 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Angebote, Order & Rechnungen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Mieten ab"
            priceFull="48"
            priceCents="90"
            details="Basierend auf 1,63 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Ideal für wachsende Unternehmen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Ratenkauf ab"
            priceFull="74"
            priceCents="90"
            details="Basierend auf 2,49 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Eigentumserwerb nach Laufzeit",
              "1 Benutzer",
            ]}
          />
        </div>

        <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
          Alle Preise exkl. MwSt.
        </div>
      </Section>
      <KassensystemHardwareSection />
      <Section variant="gray1" className="tse-section">
        <ImageText
          imagePosition="left"
          imageSrc="/tse.png"
          imageAlt="Softwareentwicklung"
          title="TSE & Rechtssicherheit: Wir machen es einfach!"
        >
          <p>
            TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
            wir kennen uns aus und übernehmen die Umsetzung der Technischen
            Sicherheitseinrichtung für Dich und informieren Dich über alle
            wichtigen Änderungen.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
              kostenlose Updates
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary " />{" "}
              <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
              und GoBD
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
              Servern und nach höchsten Standards
            </li>
          </ul>
          <p>
            Mit der Kassensoftware von SDI arbeitest Du immer 100%
            finanzamtkonform und bist automatisch auf der sicheren Seite.
          </p>
        </ImageText>
      </Section>
    </ProductPage>
  );
}
